/** @file Creates the direction set or changes its count (4 or 8); entries outside the new set are dropped. */
import { z } from 'zod';
import { throwError } from '@pose2d/format/throwError';
import type { DirectionsDoc } from '@pose2d/format/types';
import { defineCommand } from '../defineCommand';
import { pruneEntries } from './pruneEntries';

type Layout4 = 'diagonal' | 'cardinal';

/** The layout the set ends with: the given one, else the current one, else diagonal; undefined for 8 (VALIDATION with layout4). */
function layoutFor(set: DirectionsDoc | undefined, input: { count: 4 | 8; layout4?: Layout4 }): Layout4 | undefined {
  if (input.count === 4) return input.layout4 ?? set?.layout4 ?? 'diagonal';
  if (input.layout4 !== undefined) throwError('VALIDATION', undefined, { path: 'layout4', hint: 'layout4 applies to 4-direction sets only' });
  return undefined;
}

/** The layout a stored set already has: its layout4, or diagonal for a 4-set that leaves it out (R2-13 no-op). */
function storedLayout(set: DirectionsDoc): Layout4 | undefined {
  return set.layout4 ?? (set.count === 4 ? 'diagonal' : undefined);
}

/** direction.setCount: creates or resizes the set and prunes entries; the current count and layout are a no-op. */
export const command = defineCommand({
  name: 'direction.setCount',
  description:
    'Create or resize the direction set: count 4 (layout4 diagonal NE/SE/SW/NW by default, or cardinal N/E/S/W) or 8; entries the new set cannot hold are removed (a warning lists them).',
  input: z.object({ count: z.union([z.literal(4), z.literal(8)]), layout4: z.enum(['diagonal', 'cardinal']).exactOptional() }).strict(),
  apply(draft, input, ctx) {
    const layout4 = layoutFor(draft.directions, input);
    if (draft.directions?.count === input.count && storedLayout(draft.directions) === layout4) return;
    const set = (draft.directions ??= { count: input.count, entries: {} });
    set.count = input.count;
    if (layout4 === undefined) delete set.layout4;
    else set.layout4 = layout4;
    pruneEntries(set, ctx);
    draft.meta.updatedAt = ctx.now;
  },
});
